import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Bus, MapPin, Navigation, Phone, Clock, RefreshCw, 
  Play, Square, Gauge, User, Route, AlertCircle, Wifi, WifiOff
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom bus icon
const busIcon = new L.DivIcon({
  className: 'custom-bus-icon',
  html: `<div style="background: #3b82f6; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M8 6v6"/><path d="M15 6v6"/><path d="M2 12h19.6"/>
      <path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3"/>
      <circle cx="7" cy="18" r="2"/><path d="M9 18h5"/><circle cx="16" cy="18" r="2"/>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

// Stop marker icon
const stopIcon = new L.DivIcon({
  className: 'custom-stop-icon',
  html: `<div style="background: #ef4444; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 1px 4px rgba(0,0,0,0.3);"></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

// School icon
const schoolIcon = new L.DivIcon({
  className: 'custom-school-icon',
  html: `<div style="background: #10b981; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
      <path d="m4 6 8-4 8 4"/><path d="m18 10 4 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-8l4-2"/>
      <path d="M14 22v-4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v4"/><path d="M18 5v17"/><path d="M6 5v17"/>
      <circle cx="12" cy="9" r="2"/>
    </svg>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Component to recenter map
const RecenterMap = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 14);
    }
  }, [center, map]);
  return null;
};

const GPSTrackingPage = () => {
  const { getAuthHeader } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [mapCenter, setMapCenter] = useState([28.6139, 77.2090]); // Delhi default
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchVehicles();
    
    // Auto-refresh every 10 seconds
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchVehicles(true);
      }, 10000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh]);

  const fetchVehicles = async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const res = await axios.get(`${API_URL}/api/transport-v2/gps/vehicles-live`, { headers: getAuthHeader() });
      setVehicles(res.data);
      
      // Update map center to first tracking vehicle
      const trackingVehicle = res.data.find(v => v.is_tracking && v.current_location);
      if (trackingVehicle && trackingVehicle.current_location) {
        setMapCenter([trackingVehicle.current_location.lat, trackingVehicle.current_location.lng]);
      }
    } catch (error) {
      if (!silent) toast.error('Failed to fetch vehicle locations');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchVehicleDetails = async (vehicleId) => {
    try {
      const res = await axios.get(`${API_URL}/api/transport-v2/gps/vehicle/${vehicleId}`, { headers: getAuthHeader() });
      setVehicleDetails(res.data);
      setDetailsDialog(true);
      
      // Center map on vehicle
      if (res.data.vehicle?.current_location) {
        setMapCenter([res.data.vehicle.current_location.lat, res.data.vehicle.current_location.lng]);
      }
    } catch (error) {
      toast.error('Failed to fetch vehicle details');
    }
  };

  const startTracking = async (vehicleId) => {
    try {
      await axios.post(`${API_URL}/api/transport-v2/gps/start-tracking/${vehicleId}`, null, { headers: getAuthHeader() });
      toast.success('Tracking started');
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to start tracking');
    }
  };

  const stopTracking = async (vehicleId) => {
    try {
      await axios.post(`${API_URL}/api/transport-v2/gps/stop-tracking/${vehicleId}`, null, { headers: getAuthHeader() });
      toast.success('Tracking stopped');
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to stop tracking');
    }
  };

  const trackingVehicles = vehicles.filter(v => v.is_tracking && v.current_location);
  const activeVehicles = vehicles.filter(v => v.current_location);

  const formatLastUpdate = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return date.toLocaleTimeString();
  };

  return (
    <DashboardLayout title="GPS Live Tracking">
      <div className="space-y-4">
        {/* Header Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground">Total Vehicles</CardTitle>
                <Bus className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{vehicles.length}</div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground">Live Tracking</CardTitle>
                <Wifi className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{trackingVehicles.length}</div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground">With Location</CardTitle>
                <MapPin className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{activeVehicles.length}</div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setAutoRefresh(!autoRefresh)}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground">Auto Refresh</CardTitle>
                <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'text-green-500 animate-spin' : 'text-muted-foreground'}`} style={{ animationDuration: '3s' }} />
              </CardHeader>
              <CardContent>
                <div className="text-sm font-medium">{autoRefresh ? 'ON (10s)' : 'OFF'}</div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Map */}
          <div className="lg:col-span-2">
            <Card className="h-[500px]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Navigation className="h-5 w-5" /> Live Map
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={() => fetchVehicles()} disabled={refreshing}>
                    <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 h-[430px]">
                <MapContainer
                  center={mapCenter}
                  zoom={13}
                  style={{ height: '100%', width: '100%', borderRadius: '0 0 0.5rem 0.5rem' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <RecenterMap center={mapCenter} />
                  
                  {/* Vehicle Markers */}
                  {vehicles.filter(v => v.current_location).map(vehicle => (
                    <Marker
                      key={vehicle.id}
                      position={[vehicle.current_location.lat, vehicle.current_location.lng]}
                      icon={busIcon}
                      eventHandlers={{
                        click: () => fetchVehicleDetails(vehicle.id)
                      }}
                    >
                      <Popup>
                        <div className="text-sm">
                          <p className="font-bold">{vehicle.vehicle_number}</p>
                          <p>{vehicle.route_name || 'No route assigned'}</p>
                          <p className="text-xs text-muted-foreground">
                            Speed: {vehicle.current_location.speed_kmh || 0} km/h
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Updated: {formatLastUpdate(vehicle.last_location_update)}
                          </p>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                  
                  {/* Stop Markers from selected vehicle details */}
                  {vehicleDetails?.stops?.filter(s => s.latitude && s.longitude).map((stop, idx) => (
                    <Marker
                      key={stop.id}
                      position={[stop.latitude, stop.longitude]}
                      icon={stopIcon}
                    >
                      <Popup>
                        <div className="text-sm">
                          <p className="font-bold">{stop.stop_name}</p>
                          <p className="text-xs">Stop #{stop.stop_order}</p>
                          <p className="text-xs">{stop.arrival_time}</p>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </CardContent>
            </Card>
          </div>

          {/* Vehicle List */}
          <div className="space-y-4">
            <Card className="h-[500px] overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Bus className="h-5 w-5" /> Vehicles
                </CardTitle>
                <CardDescription>Click on a vehicle to view details</CardDescription>
              </CardHeader>
              <CardContent className="p-0 overflow-y-auto h-[420px]">
                {vehicles.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <Bus className="h-12 w-12 mb-2 opacity-50" />
                    <p>No vehicles found</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {vehicles.map(vehicle => (
                      <div
                        key={vehicle.id}
                        className="p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => fetchVehicleDetails(vehicle.id)}
                        data-testid={`vehicle-${vehicle.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{vehicle.vehicle_number}</p>
                              {vehicle.is_tracking ? (
                                <Badge className="bg-green-500 text-xs">
                                  <Wifi className="h-3 w-3 mr-1" /> Live
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">
                                  <WifiOff className="h-3 w-3 mr-1" /> Offline
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{vehicle.route_name || 'No route'}</p>
                            {vehicle.driver_name && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                <User className="h-3 w-3" /> {vehicle.driver_name}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            {vehicle.current_location && (
                              <>
                                <p className="text-sm font-medium flex items-center gap-1">
                                  <Gauge className="h-3 w-3" /> {vehicle.current_location.speed_kmh || 0} km/h
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {formatLastUpdate(vehicle.last_location_update)}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-800 dark:text-blue-300">GPS Tracking Information</p>
                <p className="text-blue-700 dark:text-blue-400 mt-1">
                  Location updates are received from driver's mobile app or GPS devices installed in vehicles. 
                  Make sure drivers have the tracking app installed and location permissions enabled.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vehicle Details Dialog */}
      <Dialog open={detailsDialog} onOpenChange={setDetailsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bus className="h-5 w-5" />
              {vehicleDetails?.vehicle?.vehicle_number || 'Vehicle Details'}
            </DialogTitle>
          </DialogHeader>
          
          {vehicleDetails && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info">Info</TabsTrigger>
                <TabsTrigger value="route">Route</TabsTrigger>
                <TabsTrigger value="tracking">Tracking</TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Vehicle Number</p>
                    <p className="font-medium">{vehicleDetails.vehicle?.vehicle_number}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p className="font-medium capitalize">{vehicleDetails.vehicle?.vehicle_type}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Capacity</p>
                    <p className="font-medium">{vehicleDetails.vehicle?.capacity} seats</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge className={vehicleDetails.vehicle?.is_tracking ? 'bg-green-500' : 'bg-gray-500'}>
                      {vehicleDetails.vehicle?.is_tracking ? 'Tracking' : 'Not Tracking'}
                    </Badge>
                  </div>
                </div>
                
                {vehicleDetails.driver && (
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm font-medium mb-2 flex items-center gap-2">
                      <User className="h-4 w-4" /> Driver
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{vehicleDetails.driver.name}</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {vehicleDetails.driver.phone}
                        </p>
                      </div>
                      <Button variant="outline" size="sm" asChild>
                        <a href={`tel:${vehicleDetails.driver.phone}`}>
                          <Phone className="h-4 w-4 mr-1" /> Call
                        </a>
                      </Button>
                    </div>
                  </div>
                )}

                {vehicleDetails.vehicle?.current_location && (
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm font-medium mb-2 flex items-center gap-2">
                      <MapPin className="h-4 w-4" /> Current Location
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Lat:</span> {vehicleDetails.vehicle.current_location.lat.toFixed(6)}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Lng:</span> {vehicleDetails.vehicle.current_location.lng.toFixed(6)}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Speed:</span> {vehicleDetails.vehicle.current_location.speed_kmh || 0} km/h
                      </div>
                      <div>
                        <span className="text-muted-foreground">Updated:</span> {formatLastUpdate(vehicleDetails.vehicle.last_location_update)}
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="route" className="space-y-4 mt-4">
                {vehicleDetails.route ? (
                  <>
                    <div className="p-4 border rounded-lg">
                      <p className="font-medium">{vehicleDetails.route.route_name}</p>
                      <p className="text-sm text-muted-foreground">Route #{vehicleDetails.route.route_number}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span>{vehicleDetails.route.start_point}</span>
                        <span>→</span>
                        <span>{vehicleDetails.route.end_point}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Distance: {vehicleDetails.route.distance_km} km • Est. Time: {vehicleDetails.route.estimated_time_minutes} mins
                      </p>
                    </div>
                    
                    <div className="border rounded-lg overflow-hidden">
                      <div className="p-3 bg-muted font-medium flex items-center gap-2">
                        <Route className="h-4 w-4" /> Stops ({vehicleDetails.stops?.length || 0})
                      </div>
                      <div className="divide-y max-h-[200px] overflow-y-auto">
                        {vehicleDetails.stops?.map((stop, idx) => (
                          <div key={stop.id} className="p-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                                {stop.stop_order}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{stop.stop_name}</p>
                                <p className="text-xs text-muted-foreground">{stop.landmark}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm flex items-center gap-1">
                                <Clock className="h-3 w-3" /> {stop.arrival_time}
                              </p>
                              <p className="text-xs text-muted-foreground">{stop.distance_from_school_km} km</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Route className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No route assigned to this vehicle</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="tracking" className="space-y-4 mt-4">
                <div className="flex gap-4">
                  {vehicleDetails.vehicle?.is_tracking ? (
                    <Button 
                      variant="destructive" 
                      className="flex-1"
                      onClick={() => { stopTracking(vehicleDetails.vehicle.id); setDetailsDialog(false); }}
                    >
                      <Square className="h-4 w-4 mr-2" /> Stop Tracking
                    </Button>
                  ) : (
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => { startTracking(vehicleDetails.vehicle.id); setDetailsDialog(false); }}
                    >
                      <Play className="h-4 w-4 mr-2" /> Start Tracking
                    </Button>
                  )}
                </div>
                
                <div className="p-4 bg-muted/50 rounded-lg text-sm">
                  <p className="font-medium mb-2">How GPS Tracking Works:</p>
                  <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                    <li>Driver opens the mobile tracking app</li>
                    <li>App sends location updates every 10 seconds</li>
                    <li>Parents can view live location of their child's bus</li>
                    <li>Admin can monitor all vehicles in real-time</li>
                  </ol>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default GPSTrackingPage;
